import * as t from 'io-ts';
import _, {omit} from 'lodash';

import {makeApiSerializableType, makeEnumRuntimeType} from './apiTypes';

/*
 * Enums.
 */

/** Represents workspace section types that are in the V1 format. */
enum WorkspaceSectionV1TypesEnum {
  MY_INBOX = 'my_inbox',
  TEAMS = 'teams',
  TEAMMATE_CONVERSATIONS = 'teammate_conversations',
  TEAMMATE_DELEGATIONS = 'teammate_delegations',
}

/** Represents workspace section types that are in the V2 format. */
export enum WorkspaceSectionTypesEnum {
  MY_INBOX_V2 = 'my_inbox_v2',
  // Lenses did exist when V1 was used, so only have a V2 section.
  LENSES = 'lenses',
  TEAMS_V2 = 'teams_v2',
  TEAMMATE_CONVERSATIONS_V2 = 'teammate_conversations_v2',
  TEAMMATE_DELEGATIONS_V2 = 'teammate_delegations_v2',
}

export enum WorkspaceItemTypesEnum {
  INBOX = 'inbox',
  TAG = 'tag',
  LENS = 'lens',
  TEAMMATE = 'teammate',
  DELEGATION = 'delegation',
  LABEL = 'label', // not a resource type
}

/** Labels that are sub-folders of the parent "Inbox" item. */
export enum WorkspaceFolderNestedLabelsEnum {
  ASSIGNED_TO_ME = 'assigned_to_me',
  SHARED_WITH_ME = 'shared_with_me',
  DISCUSSIONS = 'discussions',
}
export const RWorkspaceFolderNestedLabels = makeEnumRuntimeType(WorkspaceFolderNestedLabelsEnum);

/** Root-level teammate workspace labels. These are siblings--not children--of the "Inbox" item. */
export enum WorkspaceFolderRootLabelsEnum {
  STARRED = 'starred',
  DRAFTS = 'drafts',
  SENT = 'sent',
}
export const RWorkspaceFolderRootLabels = makeEnumRuntimeType(WorkspaceFolderRootLabelsEnum);

export type WorkspaceFolderLabelName = WorkspaceFolderNestedLabelsEnum | WorkspaceFolderRootLabelsEnum;

const lastViewedKey = 'last_viewed';
const isHiddenKey = 'is_hidden';

/*
 * Runtime types.
 */

const RLastViewed = t.union([t.number, t.undefined]);

const RWorkspaceFolderLabelV2 = t.type({
  name: t.string,
  type: t.literal(WorkspaceItemTypesEnum.LABEL),
  [lastViewedKey]: RLastViewed,
  [isHiddenKey]: t.boolean,
});

const RWorkspaceFolderInboxV2 = t.type({
  id: t.number,
  type: t.literal(WorkspaceItemTypesEnum.INBOX),
  [lastViewedKey]: RLastViewed,
  [isHiddenKey]: t.boolean,
});

const RWorkspaceFolderNestedItemV2 = t.union([RWorkspaceFolderLabelV2, RWorkspaceFolderInboxV2]);

const RWorkspaceInboxV2 = t.type({
  id: t.number,
  [lastViewedKey]: RLastViewed,
});

const RWorkspaceTagV2 = t.type({
  id: t.number,
  [lastViewedKey]: RLastViewed,
});

const RWorkspaceLensV2 = t.type({
  id: t.number,
  [lastViewedKey]: RLastViewed,
});

const RWorkspaceValueTeamV2 = t.readonly(
  t.type({
    inboxes: t.readonlyArray(RWorkspaceInboxV2),
    tags: t.readonlyArray(RWorkspaceTagV2),
  }),
);

const RWorkspaceValueTeamsV2 = t.record(t.string, RWorkspaceValueTeamV2);

const RWorkspaceTeammateConversationV2 = t.type({
  id: t.number,
  [lastViewedKey]: RLastViewed,
});

const RWorkspaceTeammateDelegationV2 = t.type({
  id: t.number,
});

const RWorkspaceValue = t.type({
  // Teammate workspace.
  [WorkspaceSectionTypesEnum.MY_INBOX_V2]: t.type({
    nested_folders: t.readonlyArray(RWorkspaceFolderNestedItemV2),
    root_folders: t.readonlyArray(RWorkspaceFolderLabelV2),
    tags: t.readonlyArray(RWorkspaceTagV2),
  }),

  // Lenses workspace.
  [WorkspaceSectionTypesEnum.LENSES]: t.readonlyArray(RWorkspaceLensV2),

  // Teams workspace.
  [WorkspaceSectionTypesEnum.TEAMS_V2]: RWorkspaceValueTeamsV2,

  // Teammates "Assigned conversations" workspace.
  [WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2]: t.readonlyArray(RWorkspaceTeammateConversationV2),

  // Delegations.
  [WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2]: t.readonlyArray(RWorkspaceTeammateDelegationV2),
});

export const RWorkspace = t.readonly(
  t.type({
    ownerId: t.number,
    updatedAt: t.number,
    createdAt: t.number,
    value: RWorkspaceValue,
  }),
  'workspace',
);

/*
 * Type.
 */

export interface WorkspaceModel extends t.TypeOf<typeof RWorkspace> {}

export interface WorkspaceFolderInboxV2Model extends t.TypeOf<typeof RWorkspaceFolderInboxV2> {}
export interface WorkspaceFolderLabelV2Model extends t.TypeOf<typeof RWorkspaceFolderLabelV2> {}
export type WorkspaceFolderNestedItemV2 = t.TypeOf<typeof RWorkspaceFolderNestedItemV2>;

export interface WorkspaceTagV2 extends t.TypeOf<typeof RWorkspaceTagV2> {}
export interface WorkspaceLensV2 extends t.TypeOf<typeof RWorkspaceLensV2> {}
export interface WorkspaceInboxV2 extends t.TypeOf<typeof RWorkspaceInboxV2> {}
export interface WorkspaceTeammateConversationV2 extends t.TypeOf<typeof RWorkspaceTeammateConversationV2> {}
export interface WorkspaceTeammateDelegationV2 extends t.TypeOf<typeof RWorkspaceTeammateDelegationV2> {}

export interface WorkspaceModelValueTeamsV2 extends t.TypeOf<typeof RWorkspaceValueTeamsV2> {}
export interface WorkspaceModelValueTeamV2 extends t.TypeOf<typeof RWorkspaceValueTeamV2> {}

/*
 * IO methods.
 */

export function parseWorkspace(src: any): WorkspaceModel {
  const myInboxV2 = src.value[WorkspaceSectionTypesEnum.MY_INBOX_V2] || {};

  const myInboxV2NestedFolders = myInboxV2.inbox_folders?.items;
  const myInboxV2RootFolders = myInboxV2.folders?.items;
  const myInboxV2Tags = myInboxV2.tags?.items;

  const teamsV2 = src.value[WorkspaceSectionTypesEnum.TEAMS_V2]?.items;

  const lensesV2 = src.value[WorkspaceSectionTypesEnum.LENSES]?.items;
  const delegationsV2 = src.value[WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2]?.items;
  const teammateConversationsV2 = src.value[WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2]?.items;

  return {
    updatedAt: src.updated_at,
    createdAt: src.created_at,
    ownerId: src.teammate_id,
    value: {
      // My inbox.
      [WorkspaceSectionTypesEnum.MY_INBOX_V2]: {
        nested_folders: Array.isArray(myInboxV2NestedFolders)
          ? myInboxV2NestedFolders.map((item: any) =>
              item.type === WorkspaceItemTypesEnum.LABEL
                ? parseWorkspaceFolderLabel(item)
                : parseWorkspaceFolderInbox(item),
            )
          : [],
        root_folders: Array.isArray(myInboxV2RootFolders)
          ? myInboxV2RootFolders.map((item: any) => parseWorkspaceFolderLabel(item))
          : [],
        tags: Array.isArray(myInboxV2Tags) ? myInboxV2Tags.map((item: any) => parseWorkspaceTag(item)) : [],
      },

      // Lenses.
      [WorkspaceSectionTypesEnum.LENSES]:
        (Array.isArray(lensesV2) && lensesV2.map((item: any) => parseWorkspaceLens(item))) || [],

      // Teams.
      [WorkspaceSectionTypesEnum.TEAMS_V2]: _((Array.isArray(teamsV2) && teamsV2) || [])
        .keyBy((team) => Number(team.team_id))
        .mapValues((team) => ({
          inboxes:
            (Array.isArray(team.inboxes.items) &&
              team.inboxes.items.map((item: any) => parseWorkspaceInbox(item))) ||
            [],
          tags:
            (Array.isArray(team.tags.items) && team.tags.items.map((item: any) => parseWorkspaceTag(item))) ||
            [],
        }))
        .value(),

      // Teammate conversations.
      [WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2]:
        (Array.isArray(teammateConversationsV2) &&
          teammateConversationsV2.map((item: any) => parseWorkspaceTeammateConversation(item))) ||
        [],

      // Teammate delegations.
      [WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2]:
        (Array.isArray(delegationsV2) &&
          delegationsV2.map((item: any) => parseWorkspaceTeammateDelegation(item))) ||
        [],
    },
  };
}

function serializeWorkspace(src: WorkspaceModel) {
  const myInboxV2 = src.value[WorkspaceSectionTypesEnum.MY_INBOX_V2];

  const teamsV2 = _(src.value[WorkspaceSectionTypesEnum.TEAMS_V2])
    .toPairs()
    .map(([teamId, teamData]) => {
      if (!teamData) {
        return undefined;
      }

      return {
        team_id: Number(teamId),
        inboxes: {
          items: teamData.inboxes.map((item) => omit(item, lastViewedKey)),
        },
        tags: {
          items: teamData.tags.map((item) => omit(item, lastViewedKey)),
        },
      };
    })
    .compact()
    .value();

  const teamsV1 = teamsV2.map((team) => ({
    team_id: team.team_id,
    inboxes: {
      items: team.inboxes.items.map(({id}) => id),
    },
    tags: {
      items: team.tags.items.map(({id}) => id),
    },
  }));

  const lensesV2 = src.value[WorkspaceSectionTypesEnum.LENSES];
  const delegationsV2 = src.value[WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2];
  const teammatesV2 = src.value[WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2];

  return {
    value: {
      // Workspace V2 data: personal workspace.
      [WorkspaceSectionTypesEnum.MY_INBOX_V2]: {
        inbox_folders: {
          items: myInboxV2.nested_folders.map((item) => omit(item, lastViewedKey)),
        },
        folders: {
          items: myInboxV2.root_folders.map((item) => omit(item, lastViewedKey)),
        },
        tags: {
          items: myInboxV2.tags.map((item) => omit(item, lastViewedKey)),
        },
      },
      // Workspace V1 data: personal workspace.
      [WorkspaceSectionV1TypesEnum.MY_INBOX]: {
        inbox_folders: {
          items: myInboxV2.nested_folders
            .filter((folder) => !folder[isHiddenKey])
            .map((item) => omit(item, [lastViewedKey, isHiddenKey])),
        },
        folders: {
          items: myInboxV2.root_folders
            .filter((folder) => !folder[isHiddenKey])
            .map((item) => omit(item, [lastViewedKey, isHiddenKey])),
        },
        tags: {
          items: myInboxV2.tags.map(({id}) => id),
        },
      },

      // Workspace V2 data: lenses workspace. (There is no V1 equivalent)
      [WorkspaceSectionTypesEnum.LENSES]: {
        items: lensesV2.map((item) => omit(item, lastViewedKey)),
      },

      // Workspace V2 data: team workspaces.
      [WorkspaceSectionTypesEnum.TEAMS_V2]: {
        items: teamsV2,
      },
      // Workspace V1 data: team workspaces.
      [WorkspaceSectionV1TypesEnum.TEAMS]: teamsV1,

      // Workspace V2 data: teammate conversations workspace.
      [WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2]: {
        items: teammatesV2.map((item) => omit(item, lastViewedKey)),
      },
      // Workspace V1 data: teammate conversations workspace.
      [WorkspaceSectionV1TypesEnum.TEAMMATE_CONVERSATIONS]: {
        items: teammatesV2.map(({id}) => id),
      },

      // Workspace V2 data: teammate delegation workspaces.
      [WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2]: {
        items: delegationsV2.map((item) => omit(item, lastViewedKey)),
      },
      // Workspace V1 data: teammate delegation workspaces.
      [WorkspaceSectionV1TypesEnum.TEAMMATE_DELEGATIONS]: {
        items: delegationsV2.map(({id}) => id),
      },
    },
  };
}

/*
 * API type.
 */

export const ApiWorkspace = makeApiSerializableType(
  'workspace',
  RWorkspace,
  parseWorkspace,
  serializeWorkspace,
);

/*
 * Helpers.
 */

function parseWorkspaceFolderInbox(src: any): WorkspaceFolderInboxV2Model {
  return {
    id: Number(src.id),
    type: src.type,
    [lastViewedKey]: src[lastViewedKey],
    [isHiddenKey]: Boolean(src[isHiddenKey]),
  };
}

function parseWorkspaceFolderLabel(src: any): WorkspaceFolderLabelV2Model {
  return {
    name: src.name,
    type: src.type,
    [lastViewedKey]: src[lastViewedKey],
    [isHiddenKey]: Boolean(src[isHiddenKey]),
  };
}

function parseWorkspaceTag(src: any): WorkspaceTagV2 {
  return {
    id: Number(src.id),
    [lastViewedKey]: src[lastViewedKey],
  };
}

function parseWorkspaceLens(src: any): WorkspaceLensV2 {
  return {
    id: Number(src.id),
    [lastViewedKey]: src[lastViewedKey],
  };
}

function parseWorkspaceInbox(src: any): WorkspaceInboxV2 {
  return {
    id: Number(src.id),
    [lastViewedKey]: src[lastViewedKey],
  };
}

function parseWorkspaceTeammateConversation(src: any): WorkspaceTeammateConversationV2 {
  return {
    id: Number(src.id),
    [lastViewedKey]: src[lastViewedKey],
  };
}

function parseWorkspaceTeammateDelegation(src: any): WorkspaceTeammateDelegationV2 {
  return {id: Number(src.id)};
}
